import { supabase } from '@/integrations/supabase/client';
import type { PrivateImageLibraryResponse } from '../types';

/**
 * Supabase client wrapper for private image library operations
 * Uses the PostgreSQL function for optimized queries
 */
export class PrivateImageLibraryClient {
  /**
   * Fetch paginated workbook images using PostgreSQL function
   */
  static async fetchImages(
    workbookId: string,
    userId: string,
    searchQuery?: string,
    limit: number = 12,
    offset: number = 0
  ): Promise<PrivateImageLibraryResponse[]> {
    const { data, error } = await supabase.rpc(
      'get_workbook_images_paginated_advanced' as any,
      {
        p_workbook_id: workbookId,
        p_user_id: userId,
        p_search_query: searchQuery || null,
        p_limit: limit,
        p_offset: offset
      }
    );

    if (error) {
      console.error('[PrivateImageLibraryClient] Error fetching images:', error);
      throw error;
    }

    return (data as PrivateImageLibraryResponse[]) || [];
  }

  /**
   * Get total count of images for a workbook (for quick counts)
   */
  static async getImageCount(workbookId: string, userId: string): Promise<number> {
    // Note: RLS policies handle access control (owned + collaborative access)
    const { count, error } = await supabase
      .from('workbook_images')
      .select('*', { count: 'exact', head: true })
      .eq('workbook_id', workbookId)
      .is('deleted_at', null); // Filter out soft-deleted images

    if (error) {
      console.error('[PrivateImageLibraryClient] Error getting image count:', error);
      throw error;
    }

    return count || 0;
  }
} 